<template>
  <div class="flex flex-col h-100">
    <div class="album-bar flex flex-wrap items-center">
      <div><avatar :user="album.user" /></div>
      <h1 class="ml-4">{{album.name}}</h1>
      <div class="buttoncontainer flex-wrap m-2 land:m-4 land:p-4">
        <div v-if="user && user.id === album.userId"><router-link :to="albumPages(album)" class="button">Organize Pages</router-link></div>
        <div>
          <button @click="share"><i class="fas fa-share-alt"></i> Share this {{album.pagesPerSpread === 2 ? 'spread' : 'page'}}</button>
        </div>
        <div v-if="fullscreenEnabled"><button @click="toggleFullScreen">Toggle full screen</button></div>
        <div>
          <button @click="toggleFinderView">Toggle Finder view</button>
        </div>
        <div class="hidden land:block">
          <button @click="toggleShowMagnifyingGlass">
            <!-- Known bug: The font-awesome library I'm using doesn't know about Vue, so it switches the icon to an SVG and that DOM element will never be updated so this doesn't actually change the icons. To get around, use key. -->
            <span key="fa-search-minus" v-if="showMagnifyingGlass"><i class="fas fa-search-minus"></i> Don't Magnify</span>
            <span key="fa-search-plus" v-else><i class="fas fa-search-plus"></i> Magnify</span>
          </button>
        </div>
      </div>
    </div>
    <div v-if="showFinderView">
      <finder-view
        :pages="pages"
        :currentPageIndex="currentPageIndex"
        :onClickItem="setCurrentPageIndex"
      />
    </div>
    <mobile-spread-viewer
      v-else-if="useMobileViewer"
      :album="album"
      :pages="pages"
      :currentPageIndex="currentPageIndex"
      :setCurrentPageIndex="setCurrentPageIndex"
    />
    <div v-else>
      <album-thumbnails
        :pages="pages"
        :pagesPerSpread="album.pagesPerSpread"
        :currentPageIndex="currentPageIndex"
        :onClickItem="setCurrentPageIndex"
        :itemType="album.pagesPerSpread === 2 ? 'spread' : 'page'"
        :show-on-mobile="false"
      />
      <page-turner
        :pages="pages"
        :pagesPerSpread="album.pagesPerSpread"
        :currentPageIndex="currentPageIndex"
        :setCurrentPageIndex="setCurrentPageIndex"
        :showMagnifyingGlass="showMagnifyingGlass"
      />
    </div>
  </div>
</template>

<script>
import fscreen from 'fscreen'
import { albumPages } from '../utils/routing'
import { orderPages } from '../utils'
import { viewPage } from '../utils/routing'
import AlbumThumbnails from './AlbumThumbnails'
import FinderView from './FinderView'
import Avatar from './Avatar'
import PageTurner from './PageTurner'
import MobileSpreadViewer from './MobileSpreadViewer'

const localStorageKey = 'scrapbook-share.view-album.showFinderView'
// Same as the `land` screen in tailwind.config.js. Phones (either orientation) don't match it.
const largeScreenQuery = window.matchMedia('(min-width: 640px) and (min-height: 435px)')

export default {
  props: ['album', 'user'],
  data() {
    let currentPageIndex = 0
    if (this.$route.params.pageId) {
      const pageId = parseInt(this.$route.params.pageId, 10)
      const pageIndex = orderPages(this.album.pages, this.album.pageOrders).findIndex(x => x.id === pageId)
      if (pageIndex >= 0) {
        // Always start a spread on its left page.
        currentPageIndex = pageIndex - (pageIndex % this.album.pagesPerSpread)
      }
    }

    return {
      fullscreenEnabled: fscreen.fullscreenEnabled,
      currentPageIndex,
      showMagnifyingGlass: false,
      showFinderView: this.$localStorage.get(localStorageKey, false, Boolean),
      isLargeScreen: largeScreenQuery.matches,
    }
  },
  mounted() {
    largeScreenQuery.addListener(this.onScreenSizeChange)
    // If page id from route is not part of this album, go to first page
    if (this.$route.params.pageId) {
      const pageId = parseInt(this.$route.params.pageId, 10)
      const pageIndex = orderPages(this.album.pages, this.album.pageOrders).findIndex(x => x.id === pageId)
      if (pageIndex === -1) {
        this.setCurrentPageIndex(0)
      }
    }
  },
  beforeDestroy() {
    largeScreenQuery.removeListener(this.onScreenSizeChange)
  },
  computed: {
    pages() {
      return orderPages(this.album.pages, this.album.pageOrders)
    },
    useMobileViewer() {
      return !this.isLargeScreen && this.album.pagesPerSpread === 2 && this.pages.length > 0
    },
  },
  components: {
    AlbumThumbnails,
    Avatar,
    FinderView,
    MobileSpreadViewer,
    PageTurner,
  },
  methods: {
    albumPages,
    setCurrentPageIndex(index) {
      this.currentPageIndex = index
      this.$router.history.replace(viewPage(this.album, this.pages[this.currentPageIndex].id))
    },
    // Links to the current spread (by its left page). On phones this opens the share sheet, so it can go straight into
    // a text message; elsewhere it copies the link.
    share() {
      const url = window.location.origin + this.$router.resolve(viewPage(this.album, this.pages[this.currentPageIndex].id)).href
      if (navigator.share && window.matchMedia('(hover: none)').matches) {
        navigator.share({ title: this.album.name, url }).catch(() => {
          // The user closed the share sheet. Nothing to do.
        })
        return
      }
      const copied = navigator.clipboard ? navigator.clipboard.writeText(url) : Promise.reject()
      copied.then(() => {
        this.$snotify.success('Link copied to the clipboard')
      }).catch(() => {
        this.$snotify.info(url, 'Copy this link', { timeout: 0, closeOnClick: false })
      })
    },
    toggleFullScreen() {
      if (fscreen.fullscreenElement !== null) {
        fscreen.exitFullscreen()
      } else {
        fscreen.requestFullscreen(this.$el)
      }
    },
    toggleShowMagnifyingGlass() {
      this.showMagnifyingGlass = !this.showMagnifyingGlass
    },
    onScreenSizeChange(event) {
      this.isLargeScreen = event.matches
    },
    toggleFinderView() {
      this.showFinderView = !this.showFinderView
      this.$localStorage.set(localStorageKey, this.showFinderView)
    },
  },
}
</script>

<style lang="stylus" scoped>
.album-bar
  @media only screen and (max-width: 639px)
    padding 0 10px

    h1
      font-size 20px
      margin 0 0 0 10px

    .buttoncontainer
      width 100%
      margin 8px 0 0 0

    >>> .avatar img
      max-width 36px
</style>
